import {useCallback, useState, RefObject} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import {useEnhancedRef} from '@netx/core-components/hooks/useEnhancedRef';

export type UseHeaderOverflowMenuArguments = {
	rootRef: RefObject<HTMLElement>;
	crumbsRef: RefObject<HTMLElement>;
	itemsBeforeCollapse?: number;
	itemsAfterCollapse?: number;
};

export const useBreadcrumbsOverflow = ({
	rootRef,
	crumbsRef,
	itemsAfterCollapse = 1,
	itemsBeforeCollapse = 1,
}: UseHeaderOverflowMenuArguments) => {
	const [overflown, setOverflown] = useState(false);
	const [placeIndexesInMenu, setPlaceIndexesInMenu] = useState<number[]>([]);

	const [itemsAfterCollapseRef] = useEnhancedRef(itemsAfterCollapse);
	const [itemsBeforeCollapseRef] = useEnhancedRef(itemsBeforeCollapse);

	const onResize = useCallback(() => {
		if (!rootRef.current || !crumbsRef.current) return;

		const rootEl = rootRef.current;
		const controlsEl = crumbsRef.current;

		const childDisplayStyles: Array<string | null | undefined> = [];
		const childOverflowStyles: Array<string | null | undefined> = [];

		// all children except the overflow menu element
		const childArray = Array.from(controlsEl.children || []).filter(
			(child) =>
				!child.classList.contains('hidden-menu') &&
				!child.classList.contains('breadcrumb-separator'),
		) as HTMLElement[];

		const childCount = childArray.length;

		// make all children visible while backing up original `display`
		const childWidths = childArray.map((child) => {
			const oldDisplayStyle = child.style?.display;
			const oldOverflowStyle = child.style?.overflow;
			childDisplayStyles.push(oldDisplayStyle);
			childOverflowStyles.push(oldOverflowStyle);
			// make sure we are not truncated at all
			child.style.overflow = 'visible';
			if (oldDisplayStyle) child.style.display = '';
			return child.clientWidth;
		});

		const totalChildWidth = childWidths.reduce(
			(accum, width) => accum + width,
			0,
		);

		// TODO: get the separator size from the element
		const totalSeparatorWidth = (childCount - 1) * 12.05;

		// get container measurement
		const actionsBoundingClientRect = controlsEl.getBoundingClientRect();
		const containerBoundingClientRect = rootEl.getBoundingClientRect();
		// we will get floats at times
		const containerWidth = Math.round(containerBoundingClientRect.width);
		const actionsRelativeX =
			actionsBoundingClientRect.x - containerBoundingClientRect.x;
		const actionsRelativeX2 = Math.round(
			actionsRelativeX + totalChildWidth + totalSeparatorWidth,
		);

		// put original `display` back
		childArray.forEach((child, index) => {
			if (childDisplayStyles[index])
				child.style.display = childDisplayStyles[index] || '';
			child.style.overflow = childOverflowStyles[index] || '';
		});

		const diff = actionsRelativeX2 - containerWidth;
		// TODO: deal with hard coded padding value here
		// this is the ellipsis menu 28 + separator 12
		const limitWidth = diff > 0 ? diff + 40 : diff;
		let accumWidth = 0;

		const placeIndexesInMenu =
			diff > 0
				? childWidths
						.slice(
							itemsBeforeCollapseRef.current,
							childWidths.length - itemsAfterCollapseRef.current,
						)
						.reduce<number[]>((indexes, childWidth, index) => {
							if (accumWidth > limitWidth) return indexes;
							accumWidth += childWidth + 12.05;
							return [...indexes, index + itemsBeforeCollapseRef.current];
						}, [])
				: [];

		unstable_batchedUpdates(() => {
			setOverflown(!!placeIndexesInMenu.length);
			setPlaceIndexesInMenu(placeIndexesInMenu);
		});
	}, []);

	return {
		onResize,
		overflown,
		placeIndexesInMenu,
	};
};

export default useBreadcrumbsOverflow;
